import React, { useState } from 'react';
import { Play } from 'lucide-react';

/**
 * LiteYouTube - Performance-optimized YouTube embed component
 * 
 * Loads a thumbnail image initially, only loading the full YouTube iframe
 * when the user clicks to play. This significantly improves LCP and reduces
 * initial page load time.
 * 
 * @param {string} videoId - YouTube video ID (e.g., "jWtU-goXlDw")
 * @param {string} title - Video title for accessibility
 * @param {string} className - Optional additional CSS classes
 */
const LiteYouTube = ({ videoId, title, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  if (isPlaying) {
    return (
      <div className={`relative w-full ${className}`} style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute top-0 left-0 w-full h-full"
          style={{ border: 0 }}
        />
      </div>
    );
  }

  return (
    <div className={`relative w-full cursor-pointer group ${className}`} style={{ paddingBottom: '56.25%' }} onClick={handlePlay}>
      {/* Thumbnail Image */}
      <img
        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
        alt={title}
        className="absolute top-0 left-0 w-full h-full object-cover"
        loading="lazy"
        width="1280"
        height="720"
      />
      
      {/* Dark Overlay on Hover */}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
      
      {/* Play Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 md:w-24 md:h-24 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
          <Play className="w-10 h-10 md:w-12 md:h-12 text-white ml-1" fill="white" />
        </div>
      </div>

      {/* Video Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-white text-sm md:text-base font-medium truncate">{title}</p>
      </div>
    </div>
  );
};

export default LiteYouTube;
